'use client';

import { type MouseEventHandler, forwardRef, useCallback, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { ToggleCardRootContext } from './context/toggle-card-root.context';
import { toggleCardRootRecipe } from './styles/toggle-card-root.styles';
import { ToggleCardRootContent } from './toggle-card-root-content';
import { ToggleCardRootTrigger } from './toggle-card-root-trigger';
import type { ToggleCardRootPropsInterface } from './types/toggle-card-root.types';

/**
 * Toggle card root. Selection is driven by an empty stretched native `<button>`
 * so card content is not announced as the control name.
 *
 * Prefer a `ToggleCard.Header` for the accessible name via `aria-labelledby`.
 * Pass `aria-label` when there is no header, or to override the header name.
 *
 * @since 10.0.0
 */
export const ToggleCardRoot = forwardRef<HTMLDivElement, ToggleCardRootPropsInterface>(
  (props: ToggleCardRootPropsInterface, forwardedRef) => {
    const {
      className,
      css: cssProp,
      children,
      disabled = false,
      elevated = true,
      outlined = true,
      tone = 'neutral',
      selected,
      defaultSelected,
      onSelectedChange,
      onClick,
      onKeyDown,
      renderTrigger,
      renderContent,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedby,
      'aria-roledescription': ariaRoledescription,
      ...rest
    } = props;

    const [labelId, setLabelId] = useState<string | undefined>(undefined);

    const [selectedState, setSelectedState] = useControllableValue<boolean | undefined>({
      controlledValue: selected,
      uncontrolledValue: defaultSelected,
      isControlled: 'selected' in props,
      isUncontrolled: 'defaultSelected' in props,
      onChange: (newSelected) => {
        if (newSelected !== undefined) {
          onSelectedChange?.(newSelected);
        }
      },
      componentName: 'ToggleCard.Root',
      controlledPropName: 'selected',
      uncontrolledPropName: 'defaultSelected',
    });

    const toggle: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        onClick?.(e);
        setSelectedState((prev) => !prev);
      },
      [onClick, setSelectedState],
    );

    const contextValue = useMemo(
      () => ({
        setLabelId,
        triggerProps: {
          'aria-label': ariaLabel,
          /*
           * `aria-labelledby` wins over `aria-label` in name computation, so drop
           * the header reference when an explicit `aria-label` is given.
           */
          'aria-labelledby': ariaLabel ? undefined : labelId,
          'aria-describedby': ariaDescribedby,
          'aria-roledescription': ariaRoledescription,
          'onClick': toggle,
          'onKeyDown': onKeyDown,
        },
        rootProps: { disabled, elevated, outlined, selected: !!selectedState, tone },
      }),
      [
        ariaDescribedby,
        ariaLabel,
        ariaRoledescription,
        disabled,
        elevated,
        labelId,
        onKeyDown,
        outlined,
        selectedState,
        toggle,
        tone,
      ],
    );

    const styles = css(toggleCardRootRecipe.raw({ disabled }), cssProp);

    return (
      <ToggleCardRootContext.Provider value={contextValue}>
        <div
          ref={forwardedRef}
          data-c11n-component="ToggleCard.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_toggle-card__root', styles, className)}
          {...rest}
        >
          {renderTrigger ? renderTrigger() : <ToggleCardRootTrigger />}
          {renderContent ? (
            renderContent({ children })
          ) : (
            <ToggleCardRootContent>{children}</ToggleCardRootContent>
          )}
        </div>
      </ToggleCardRootContext.Provider>
    );
  },
);

ToggleCardRoot.displayName = 'ToggleCard.Root';
